"use client";

import dynamic from "next/dynamic";
import Image from "next/image";
import { Component, useCallback, useEffect, useId, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { PieceArt } from "@/components/board/PieceArt";
import { useAppearance } from "@/components/site/AppearanceProvider";
import type { ExploreItem } from "@/lib/explore";
import type { PreviewCameraHandle, PreviewPose } from "./previewCamera";

const Scene = dynamic(() => import("./DiscoveryScene"), { ssr: false });
let supported: boolean | undefined;
function supports3D() {
  if (supported !== undefined) return supported;
  try {
    const context = document.createElement("canvas").getContext("webgl2");
    supported = Boolean(context);
    context?.getExtension("WEBGL_lose_context")?.loseContext();
  } catch { supported = false; }
  return supported;
}

const coarseQuery = "(pointer: coarse)";
function subscribePointer(callback: () => void) {
  const media = window.matchMedia(coarseQuery);
  media.addEventListener("change", callback);
  return () => media.removeEventListener("change", callback);
}
const getCoarsePointer = () => window.matchMedia(coarseQuery).matches;
const serverPointer = () => false;

class PreviewBoundary extends Component<{ children: ReactNode; onFailure: () => void }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { this.props.onFailure(); }
  render() { return this.state.failed ? null : this.props.children; }
}

function ControlIcon({ name }: { name: "in" | "out" | "reset" | "orbit" }) {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {name === "reset" ? <path d="M4 10a8 8 0 1 1 1 7M4 4v6h6" /> : name === "orbit" ? <><ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(-30 12 12)" /><circle cx="12" cy="12" r="3" /><path d="m17 3 3 2-1 3" /></> : <><circle cx="10" cy="10" r="6.5" /><path d="m15 15 5 5M7 10h6" />{name === "in" && <path d="M10 7v6" />}</>}
  </svg>;
}

/** Demand-rendered viewers release offscreen contexts and remember their individual camera poses. */
export function DiscoveryVisual({ item, large = false }: { item: Pick<ExploreItem, "title" | "href" | "image" | "visual">; large?: boolean }) {
  const host = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const api = useRef<PreviewCameraHandle>(null);
  const pose = useRef<PreviewPose | null>(null);
  const [visible, setVisible] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [active, setActive] = useState(false);
  const coarse = useSyncExternalStore(subscribePointer, getCoarsePointer, serverPointer);
  const hint = useId();
  const onReady = useCallback(() => setReady(true), []);
  const onFailure = useCallback(() => { setFailed(true); setReady(false); setActive(false); }, []);
  const { appearance } = useAppearance();
  const seed = [...item.href].reduce((hash, letter) => (hash * 31 + letter.charCodeAt(0)) >>> 0, 7);
  useEffect(() => {
    if (!host.current || item.image) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!supports3D()) { onFailure(); return; }
      setVisible(entry.isIntersecting);
      if (!entry.isIntersecting) { setActive(false); setReady(false); }
    }, { threshold: .05 });
    observer.observe(host.current);
    return () => observer.disconnect();
  }, [item.image, onFailure]);
  return <div ref={host} className={`discovery-visual discovery-palette-${seed % 5}${large ? " discovery-visual-large" : ""}${active ? " is-interacting" : ""}${ready ? " is-ready" : ""}${coarse ? " is-touch" : ""}${item.image ? " is-image" : ""}`}
    onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setActive(false); }}
    onPointerLeave={(event) => { if (event.pointerType === "mouse" && event.buttons === 0) setActive(false); }}>
    {item.image ? <Image src={item.image} alt="" fill sizes="(max-width:700px) 100vw, (max-width:1100px) 50vw, 420px" /> : <>
      <div ref={stage} className="discovery-stage" role="group" aria-label={`3D preview: ${item.title}`} aria-describedby={hint} tabIndex={ready ? 0 : -1}
        onFocus={(event) => { if (event.target === event.currentTarget && ready) setActive(true); }}
        onPointerDownCapture={(event) => {
          if (event.pointerType === "mouse" && ready && !(event.target as HTMLElement).closest("button")) {
            setActive(true); stage.current?.focus({ preventScroll: true });
          }
        }}
        onKeyDown={(event) => {
          if (!ready || event.target !== event.currentTarget) return;
          const actions: Record<string, () => void> = {
            ArrowLeft: () => api.current?.rotate(-1), ArrowRight: () => api.current?.rotate(1),
            ArrowUp: () => api.current?.tilt(-1), ArrowDown: () => api.current?.tilt(1),
            "+": () => api.current?.zoom(1), "=": () => api.current?.zoom(1), "-": () => api.current?.zoom(-1),
            Home: () => api.current?.reset(), r: () => api.current?.reset(), Escape: () => setActive(false),
          };
          if (actions[event.key]) { event.preventDefault(); actions[event.key](); }
        }}>
        <div className="discovery-visual-fallback" aria-hidden="true"><PieceArt type={item.visual.piece ?? "n"} color="w" finish="ivory" /><span className="discovery-visual-halo" /></div>
        {visible && !failed && <div className="discovery-canvas" aria-hidden="true"><PreviewBoundary onFailure={onFailure}><Scene visual={item.visual} seed={seed} light={appearance.theme === "light"} active={active} coarse={coarse} apiRef={api} poseRef={pose} onReady={onReady} onFailure={onFailure} /></PreviewBoundary></div>}
        <span className="discovery-visual-mark" aria-hidden="true">{failed ? "The chess collection" : ready ? active ? coarse ? "Drag to rotate · Pinch to zoom" : "Drag to rotate · Scroll to zoom" : coarse ? "A closer look, at your fingertips" : "Drag to discover every angle" : "Preparing your 3D preview"}<span>✧</span></span>
      </div>
      <p id={hint} className="sr-only">{failed ? "3D is unavailable on this device. Open the discovery below to continue." : "Drag to rotate. Enable Rotate & zoom to zoom with the scroll wheel or pinch on touch screens. Keyboard: arrow keys rotate, plus and minus zoom, Home resets, Escape releases zoom."}</p>
      <div className="discovery-viewer-toolbar" role="group" aria-label={`Preview controls: ${item.title}`}>
        <button className="discovery-interact" aria-pressed={active} disabled={!ready} onClick={() => setActive(!active)}><ControlIcon name="orbit" /><span>{failed ? "Preview unavailable" : active ? "Done exploring" : "Rotate & zoom"}</span></button>
        <div className="discovery-camera-controls">
          <button disabled={!ready} aria-label={`Zoom out on ${item.title}`} title="Zoom out (−)" onClick={() => api.current?.zoom(-1)}><ControlIcon name="out" /></button>
          <button disabled={!ready} aria-label={`Zoom in on ${item.title}`} title="Zoom in (+)" onClick={() => api.current?.zoom(1)}><ControlIcon name="in" /></button>
          <button disabled={!ready} aria-label={`Reset ${item.title} preview`} title="Reset view (Home)" onClick={() => api.current?.reset()}><ControlIcon name="reset" /></button>
        </div>
      </div>
    </>}
  </div>;
}
